import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const isAllowed = (file) => /^image\/(jpeg|jpg|png)$/i.test(file.type);
const prettySize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * File picker that shows the chosen photo(s) and waits for confirmation
 * before handing them to `onConfirm`. Nothing is uploaded until the user says so.
 */
export const PhotoPicker = ({
  children,
  multiple = false,
  maxSizeMb = 6,
  maxFiles,
  uploading = false,
  disabled = false,
  onConfirm,
  triggerClassName = '',
  testId = 'photo-picker',
  title,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [error, setError] = useState('');
  const [rejected, setRejected] = useState('');

  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const reset = () => {
    previews.forEach((url) => URL.revokeObjectURL(url));
    setFiles([]);
    setPreviews([]);
    setError('');
  };

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    let message = '';
    let valid = picked.filter((f) => {
      if (!isAllowed(f)) { message = t('photoPicker.onlyImages', 'Only JPG or PNG images are allowed'); return false; }
      if (f.size > maxSizeMb * 1024 * 1024) {
        message = t('photoPicker.tooBig', 'Each photo must be under {{mb}}MB', { mb: maxSizeMb });
        return false;
      }
      return true;
    });
    if (maxFiles && valid.length > maxFiles) {
      valid = valid.slice(0, maxFiles);
      message = t('photoPicker.tooMany', 'You can add {{count}} more photo(s)', { count: maxFiles });
    }
    if (!valid.length) {
      setRejected(message || t('photoPicker.cannotUse', 'That file cannot be used'));
      setTimeout(() => setRejected(''), 6000);
      return;
    }

    setRejected('');
    previews.forEach((url) => URL.revokeObjectURL(url));
    setFiles(valid);
    setPreviews(valid.map((f) => URL.createObjectURL(f)));
    setError(message);
  };

  const removeAt = (index) => {
    URL.revokeObjectURL(previews[index]);
    const nextFiles = files.filter((_, i) => i !== index);
    const nextPreviews = previews.filter((_, i) => i !== index);
    setFiles(nextFiles);
    setPreviews(nextPreviews);
    if (!nextFiles.length) setError('');
  };

  const confirm = async () => {
    const chosen = files;
    await onConfirm(chosen);
    reset();
  };

  return (
    <>
      <label className={`cursor-pointer ${triggerClassName}`} data-testid={`${testId}-trigger`}>
        {children}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          multiple={multiple}
          onChange={handlePick}
          className="hidden"
          disabled={disabled || uploading}
          data-testid={`${testId}-input`}
        />
      </label>

      {rejected && (
        <p className="mt-2 text-xs font-semibold text-rose-600" data-testid={`${testId}-rejected`}>
          {rejected}
        </p>
      )}

      {previews.length > 0 && (
        <div
          className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/60 p-4 backdrop-blur-sm"
          data-testid={`${testId}-preview-modal`}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-base font-bold text-gray-900">
                  {title || t('photoPicker.title', 'Check your photo before uploading')}
                </h4>
                <p className="mt-1 text-xs text-gray-500">
                  {multiple
                    ? t('photoPicker.hintMulti', 'These are the photos you picked. Remove any you do not want, then upload.')
                    : t('photoPicker.hintSingle', 'This is how your photo looks. Upload it, or pick a different one.')}
                </p>
              </div>
              <button
                onClick={reset}
                disabled={uploading}
                data-testid={`${testId}-cancel-btn`}
                className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                aria-label="Cancel"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className={`mt-4 grid gap-3 ${multiple ? 'grid-cols-3' : 'grid-cols-1'}`}>
              {previews.map((url, i) => (
                <div key={url} className="relative" data-testid={`${testId}-preview-${i}`}>
                  <img
                    src={url}
                    alt={files[i]?.name || 'Selected'}
                    className={`w-full rounded-xl border border-gray-200 object-cover ${multiple ? 'aspect-square' : 'max-h-72'}`}
                  />
                  {multiple && previews.length > 1 && (
                    <button
                      onClick={() => removeAt(i)}
                      disabled={uploading}
                      data-testid={`${testId}-remove-${i}`}
                      className="absolute top-1 end-1 rounded-full bg-red-600 p-1 text-white shadow-md hover:bg-red-700 disabled:opacity-50"
                      aria-label="Remove photo"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {!multiple && files[i] && (
                    <p className="mt-2 truncate text-center text-xs text-gray-500">
                      {files[i].name} · {prettySize(files[i].size)}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <p className="mt-3 text-xs font-semibold text-amber-600" data-testid={`${testId}-warning`}>
                {error}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid={`${testId}-change-btn`}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('photoPicker.chooseAnother', 'Choose another')}
              </button>
              <button
                onClick={confirm}
                disabled={uploading}
                data-testid={`${testId}-confirm-btn`}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {uploading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                {uploading
                  ? t('photoPicker.uploading', 'Uploading…')
                  : multiple
                    ? t('photoPicker.uploadCount', 'Upload {{count}} photos', { count: files.length })
                    : t('photoPicker.useThis', 'Use this photo')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoPicker;
